import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1765046255462-198d49d07dd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMG1pbmltYWxpc3QlMjBkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3ODU4Mzg0MTR8MA&ixlib=rb-4.1.0&q=85";

const reveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={reveal} initial="hidden" animate="show" custom={i} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax abstract element */}
      <motion.div
        style={{ y: imgY }}
        className="pointer-events-none absolute right-[-6%] top-[12%] w-[52vw] max-w-[760px] aspect-[4/3] opacity-70 mix-blend-multiply"
        aria-hidden="true"
      >
        <motion.img
          src={HERO_IMG}
          alt=""
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="h-full w-full object-cover grayscale"
        />
      </motion.div>

      <div className="relative mx-auto flex min-h-screen max-w-[1600px] flex-col justify-end px-6 pb-16 md:px-12 md:pb-24">
        <motion.div style={{ y, opacity }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 flex flex-wrap items-center gap-4"
          >
            <span className="overline">Portfolio — 2025</span>
            <span className="h-px w-16 bg-[#0A0A0A]/30" />
            <span className="overline">Data Science &amp; Machine Learning</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="availability-badge"
            className="mb-8 inline-flex items-center gap-3 border border-[#0A0A0A]/15 bg-white/60 px-4 py-2 backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-600" />
            </span>
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A]">
              Available for apprenticeship — 1 wk school / 2–3 wk enterprise
            </span>
          </motion.div>

          <h1 className="font-display font-medium leading-[0.9] tracking-tight text-[#0A0A0A] text-[15vw] md:text-[12vw] lg:text-[10.5rem]">
            <Line i={0}>WOTOBE</Line>
            <Line i={1}>
              <span className="italic text-[#5C5C5C]">Koffi</span> Roland
            </Line>
          </h1>

          <div className="mt-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-md font-sans text-base leading-relaxed text-[#5C5C5C]"
            >
              A data scientist turning raw, messy data into models that measure risk,
              forecast outcomes, and support real decisions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
              className="flex items-center gap-8"
            >
              <a
                href="#work"
                data-testid="hero-cta"
                className="group inline-flex items-center gap-3 font-mono text-[0.72rem] uppercase tracking-[0.2em]"
              >
                <span className="relative">
                  View selected work
                  <span className="absolute left-0 -bottom-1 h-px w-full bg-[#0A0A0A] origin-left scale-x-100 transition-transform duration-500 group-hover:scale-x-0" />
                </span>
                <span className="inline-block transition-transform duration-500 group-hover:translate-y-1">↓</span>
              </a>

              <a
                href="/wotobe-koffi-roland-cv.pdf"
                download
                data-testid="hero-cv"
                className="group inline-flex items-center gap-3 font-mono text-[0.72rem] uppercase tracking-[0.2em] text-[#5C5C5C] transition-colors duration-300 hover:text-[#0A0A0A]"
              >
                <span className="relative">
                  Download CV
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#0A0A0A] transition-all duration-500 group-hover:w-full" />
                </span>
                <span className="inline-block transition-transform duration-500 group-hover:translate-y-1">↓</span>
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
